import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, MailCheck, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { FormField } from '@/components/shared/form-field';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getErrorMessage } from '@/lib/errors';
import { FunctionCallError } from '@/lib/supabase/functions';

import {
  completeInvitation,
  fetchOwnInvitation,
  InvitationLinkError,
  setSessionFromInviteLink,
  signInWithPassword,
  verifyInvitationToken,
  type OwnInvitation,
} from '../api';
import { useAuth } from '../auth-context';
import { AuthLayout } from '../components/auth-layout';
import { FormAlert } from '../components/form-alert';
import { AuthSplash } from '../components/full-page-state';
import { PasswordInput } from '../components/password-input';
import { profileKeys } from '../hooks/use-profile';
import { acceptInvitationSchema, type AcceptInvitationValues } from '../schemas';
import { roleLabels } from '../types';

export const ACCEPT_INVITATION_PATH = '/aceptar-invitacion';

const invitationKey = (userId: string) => ['own-invitation', userId] as const;

/** Codes returned by user-admin that mean the invitation can no longer be used. */
const INVALID_CODES = new Set([
  'INVITATION_CANCELLED',
  'INVITATION_NOT_FOUND',
  'INVITATION_MISMATCH',
  'UNAUTHENTICATED',
]);

function InvalidInvitation() {
  const { status, signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <AuthLayout
      title="Esta invitación ya no es válida."
      description="Solicita a un Superadministrador de SORAI una nueva invitación."
    >
      <FormAlert>
        El enlace expiró, ya fue utilizado o la invitación fue cancelada. Si ya activaste tu cuenta,
        inicia sesión con tu correo y contraseña.
      </FormAlert>
      <Button
        className="mt-6 w-full"
        variant="secondary"
        onClick={() => {
          // Drop any half-onboarded session before going to the login page.
          void (status === 'authenticated' ? signOut() : Promise.resolve()).then(() =>
            navigate('/login', { replace: true }),
          );
        }}
      >
        Ir al inicio de sesión
      </Button>
    </AuthLayout>
  );
}

/**
 * Public, login-only onboarding route. It works ONLY with a session obtained
 * from an administrative invitation link; it is not a registration flow.
 */
export function AcceptInvitationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { status, session } = useAuth();
  const [linkError, setLinkError] = useState<InvitationLinkError | null>(null);
  const [verifying, setVerifying] = useState(false);

  const search = new URLSearchParams(location.search);
  const tokenHash = search.get('type') === 'invite' ? search.get('token_hash') : null;
  const hash = new URLSearchParams(location.hash.replace(/^#/, ''));
  const hashError = hash.has('error') || hash.has('error_code');
  const accessToken = hash.get('access_token');
  const refreshToken = hash.get('refresh_token');

  // Default Supabase template: tokens arrive in the hash (already verified by Auth).
  useEffect(() => {
    if (!accessToken || !refreshToken) return;
    let active = true;
    setSessionFromInviteLink(accessToken, refreshToken)
      .then(() => {
        if (active) void navigate(ACCEPT_INVITATION_PATH, { replace: true });
      })
      .catch((error: unknown) => {
        if (active) {
          setLinkError(
            error instanceof InvitationLinkError ? error : new InvitationLinkError('invalid'),
          );
        }
      });
    return () => {
      active = false;
    };
  }, [accessToken, refreshToken, navigate]);

  const userId = session?.user.id;
  const onboarding = !tokenHash && !accessToken && !hashError && !linkError;
  const invitation = useQuery({
    queryKey: invitationKey(userId ?? 'anonymous'),
    queryFn: () => fetchOwnInvitation(userId ?? ''),
    enabled: onboarding && status === 'authenticated' && userId !== undefined,
    staleTime: 0,
  });

  if (hashError || linkError?.kind === 'invalid') return <InvalidInvitation />;

  // Custom template: the token is verified only when the person continues, so
  // mail scanners that prefetch links cannot consume the invitation.
  if (tokenHash) {
    return (
      <AuthLayout
        title="Has sido invitado a SORAI ERP"
        description="Tu acceso fue creado por un administrador de SORAI."
      >
        <div className="space-y-4">
          {linkError && <FormAlert>{linkError.message}</FormAlert>}
          <div className="flex items-start gap-3 rounded-md border border-border bg-surface-muted px-3 py-3 text-sm text-muted-foreground">
            <MailCheck className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
            <p>Continúa para crear tu contraseña y activar tu cuenta.</p>
          </div>
          <Button
            className="w-full"
            size="lg"
            loading={verifying}
            onClick={() => {
              setVerifying(true);
              setLinkError(null);
              verifyInvitationToken(tokenHash)
                .then(() => navigate(ACCEPT_INVITATION_PATH, { replace: true }))
                .catch((error: unknown) => {
                  setLinkError(
                    error instanceof InvitationLinkError
                      ? error
                      : new InvitationLinkError('invalid'),
                  );
                })
                .finally(() => {
                  setVerifying(false);
                });
            }}
          >
            Continuar
            {!verifying && <ArrowRight aria-hidden="true" />}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (accessToken || status === 'loading') return <AuthSplash label="Validando invitación…" />;
  if (status === 'unauthenticated' || !session) return <InvalidInvitation />;
  if (invitation.isPending) return <AuthSplash label="Cargando invitación…" />;
  if (invitation.isError) {
    return (
      <AuthLayout title="No se pudo cargar tu invitación">
        <FormAlert>{getErrorMessage(invitation.error)}</FormAlert>
        <Button
          className="mt-6 w-full"
          variant="secondary"
          loading={invitation.isFetching}
          onClick={() => void invitation.refetch()}
        >
          Reintentar
        </Button>
      </AuthLayout>
    );
  }

  const current = invitation.data;
  if (current?.accepted_at) return <Navigate to="/dashboard" replace />;
  if (!current || current.cancelled_at) return <InvalidInvitation />;

  return <AcceptInvitationForm invitation={current} email={session.user.email ?? current.email} />;
}

function AcceptInvitationForm({ invitation, email }: { invitation: OwnInvitation; email: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AcceptInvitationValues>({
    resolver: zodResolver(acceptInvitationSchema),
    defaultValues: { fullName: invitation.full_name, password: '', confirmPassword: '' },
  });

  if (invalid) return <InvalidInvitation />;

  const onSubmit = handleSubmit(async ({ fullName, password }) => {
    setFormError(null);
    try {
      await completeInvitation({ password, fullName });
    } catch (error) {
      if (error instanceof FunctionCallError) {
        if (error.code === 'INVITATION_ALREADY_ACCEPTED') {
          void navigate('/login', { replace: true });
          return;
        }
        if ((error.code && INVALID_CODES.has(error.code)) || error.status === 401) {
          setInvalid(true);
          return;
        }
      }
      setFormError(getErrorMessage(error, 'No se pudo activar tu cuenta. Inténtalo nuevamente.'));
      return;
    }

    // The account is active; the link session ended when the password was set.
    try {
      await signInWithPassword(email, password);
      await queryClient.invalidateQueries({ queryKey: profileKeys.all });
      toast.success('Tu cuenta está activa. Bienvenido a SORAI ERP.');
      void navigate('/dashboard', { replace: true });
    } catch {
      toast.success('Tu cuenta está activa. Inicia sesión con tu nueva contraseña.');
      void navigate('/login', { replace: true });
    }
  });

  return (
    <AuthLayout
      title="Has sido invitado a SORAI ERP"
      description="Crea tu contraseña para activar tu cuenta."
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {formError && <FormAlert>{formError}</FormAlert>}

        <dl className="grid gap-3 rounded-md border border-border bg-surface-muted px-3 py-3 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">Correo</dt>
            <dd className="font-medium break-all text-foreground">{email}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Rol asignado</dt>
            <dd className="mt-0.5">
              <Badge tone={invitation.role === 'SUPERADMIN' ? 'brand' : 'neutral'}>
                <ShieldCheck className="size-3" aria-hidden="true" />
                {roleLabels[invitation.role]}
              </Badge>
            </dd>
          </div>
        </dl>

        <FormField label="Nombre" error={errors.fullName?.message} required>
          {(control) => (
            <Input {...control} {...register('fullName')} autoComplete="name" maxLength={120} />
          )}
        </FormField>
        <FormField
          label="Crear contraseña"
          error={errors.password?.message}
          description="Usa al menos 8 caracteres."
          required
        >
          {(control) => (
            <PasswordInput {...control} {...register('password')} autoComplete="new-password" />
          )}
        </FormField>
        <FormField label="Confirmar contraseña" error={errors.confirmPassword?.message} required>
          {(control) => (
            <PasswordInput
              {...control}
              {...register('confirmPassword')}
              autoComplete="new-password"
            />
          )}
        </FormField>

        <Button type="submit" className="w-full" size="lg" loading={isSubmitting}>
          Activar mi cuenta
        </Button>
      </form>
    </AuthLayout>
  );
}
